import React, {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import axios from 'axios';
import Cropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import ConfirmPopup from './ConfirmPopup.js';
import getProfileInitials from '../utils/getProfileInitials.js';

const defaultTexts = {
	selectSourceHeader: 'Profilová fotka',
	selectSourceMessage: 'Vyber zdroj nové profilové fotky.',
	cropHeader: 'Ořez profilové fotky',
	deleteHeader: 'Smazání profilové fotky',
	deleteMessage: 'Opravdu smazat profilovou fotku?',
	choosePhotoButton: 'Vybrat fotku',
	takePhotoButton: 'Vyfotit',
	zoomLabel: 'Přiblížení',
	emptyCropMessage: 'Vyber fotku pro ořez.',
	missingCropMessage: 'Nejdřív vyber a ořízni fotku.',
	invalidFileMessage: 'Vyber obrázek ve formátu JPG, PNG nebo WEBP.',
	uploadFailedMessage: 'Upload fotky se nepodařil.',
	deleteFailedMessage: 'Smazání fotky se nepodařilo.',
	savePhotoButton: 'Uložit fotku',
	savingPhotoButton: 'Ukládám...',
	cancelButton: 'Zrušit',
	deletingPhotoMessage: 'Mažu fotku...',
	previewDeleteButton: 'Smazat',
	previewUploadButton: 'Nahrát'
};

function createImage(url) {
	return new Promise((resolve, reject) => {
		const image = new Image();

		image.addEventListener('load', () => resolve(image));
		image.addEventListener('error', (error) => reject(error));
		image.src = url;
	});
}

async function getCroppedImageBlob(imageSrc, croppedAreaPixels) {
	const image = await createImage(imageSrc);
	const canvas = document.createElement('canvas');
	const context = canvas.getContext('2d');

	canvas.width = 640;
	canvas.height = 640;

	context.imageSmoothingEnabled = true;
	context.imageSmoothingQuality = 'high';
	context.drawImage(
		image,
		Math.round(croppedAreaPixels.x),
		Math.round(croppedAreaPixels.y),
		Math.round(croppedAreaPixels.width),
		Math.round(croppedAreaPixels.height),
		0,
		0,
		640,
		640
	);

	return new Promise((resolve, reject) => {
		canvas.toBlob((blob) => {
			if (!blob) {
				reject(new Error('Nepodařilo se připravit obrázek.'));
				return;
			}

			resolve(blob);
		}, 'image/jpeg', 0.92);
	});
}

function normalizeDimension(value, fallbackValue) {
	if (typeof value === 'number') {
		return `${value}px`;
	}

	if (typeof value === 'string' && value.trim() !== '') {
		return value;
	}

	return fallbackValue;
}

export default function EditableProfilePhoto({
	photoUrl = '',
	displayName = '',
	avatarWidth = '7rem',
	avatarHeight = '7rem',
	canEditPhoto = false,
	uploadUrl = '',
	deleteUrl = '',
	entityKey = '',
	uploadFieldName = 'PROFILE_PHOTO',
	uploadFileName = 'profilephoto.jpg',
	onPhotoChange = null,
	onPreparedPhoto = null,
	texts = {}
}) {
	const selectInputRef = useRef(null);
	const cameraInputRef = useRef(null);
	const [showSourcePopup, setShowSourcePopup] = useState(false);
	const [showCropPopup, setShowCropPopup] = useState(false);
	const [showDeletePopup, setShowDeletePopup] = useState(false);
	const [showPreviewPopup, setShowPreviewPopup] = useState(false);
	const [crop, setCrop] = useState({x: 0, y: 0});
	const [zoom, setZoom] = useState(1);
	const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
	const [selectedImageUrl, setSelectedImageUrl] = useState('');
	const [displayPhotoUrl, setDisplayPhotoUrl] = useState(photoUrl || '');
	const [imageLoadError, setImageLoadError] = useState(false);
	const [uploadingPhoto, setUploadingPhoto] = useState(false);
	const [deletingPhoto, setDeletingPhoto] = useState(false);
	const [uploadMessage, setUploadMessage] = useState('');
	const labels = {...defaultTexts, ...texts};
	const prepareOnly = typeof onPreparedPhoto === 'function';
	const showPhotoImage = displayPhotoUrl !== '' && !imageLoadError;
	const zoomRangeId = `usrprofile_zoom_range_${String(entityKey || 'default').replace(/[^a-zA-Z0-9_-]/g, '_')}`;
	const resolvedAvatarSize = normalizeDimension(avatarWidth || avatarHeight, '7rem');
	const mediaStyle = {
		flex: `0 0 ${resolvedAvatarSize}`,
		width: resolvedAvatarSize,
		height: resolvedAvatarSize
	};
	const avatarStyle = {
		width: resolvedAvatarSize,
		height: resolvedAvatarSize
	};
	const initials = getProfileInitials(displayName);
	const resolvedEntityKey = entityKey !== null && typeof entityKey !== 'undefined' && String(entityKey).trim() !== '' ? String(entityKey) : '';
	const renderPopup = (popupNode) => {
		if (typeof document === 'undefined') {
			return popupNode;
		}

		return createPortal(popupNode, document.body);
	};

	useEffect(() => {
		setDisplayPhotoUrl(photoUrl || '');
		setImageLoadError(false);
	}, [photoUrl, entityKey]);

	useEffect(() => {
		return () => {
			if (selectedImageUrl) {
				URL.revokeObjectURL(selectedImageUrl);
			}
		};
	}, [selectedImageUrl]);

	const closeSourcePopup = () => {
		setShowSourcePopup(false);
	};

	const closeDeletePopup = () => {
		setShowDeletePopup(false);
	};

	const closePreviewPopup = () => {
		setShowPreviewPopup(false);
	};

	const closeCropPopup = () => {
		setShowCropPopup(false);
		setCrop({x: 0, y: 0});
		setZoom(1);
		setCroppedAreaPixels(null);
		setUploadMessage('');

		if (selectedImageUrl) {
			URL.revokeObjectURL(selectedImageUrl);
			setSelectedImageUrl('');
		}
	};

	const updatePhoto = (nextPhotoUrl, responseData = null) => {
		setDisplayPhotoUrl(nextPhotoUrl || '');
		setImageLoadError(false);

		if (typeof onPhotoChange === 'function') {
			onPhotoChange(nextPhotoUrl || '', responseData);
		}
	};

	const handleEditPhotoClick = () => {
		if (!canEditPhoto || (!uploadUrl && !prepareOnly)) {
			return;
		}

		setUploadMessage('');
		setShowSourcePopup(true);
	};

	const handlePreviewPhotoClick = () => {
		setShowPreviewPopup(true);
	};

	const handlePreviewUploadClick = () => {
		closePreviewPopup();
		handleEditPhotoClick();
	};

	const handleDeletePhotoClick = () => {
		setUploadMessage('');
		setShowDeletePopup(true);
	};

	const handlePreviewDeleteClick = () => {
		closePreviewPopup();
		handleDeletePhotoClick();
	};

	const handleImageSelected = (event) => {
		const file = event.target.files?.[0];

		if (!file) {
			return;
		}

		if (!file.type.startsWith('image/')) {
			setUploadMessage(labels.invalidFileMessage);
			setShowCropPopup(true);
			event.target.value = '';
			return;
		}

		const nextImageUrl = URL.createObjectURL(file);

		if (selectedImageUrl) {
			URL.revokeObjectURL(selectedImageUrl);
		}

		setSelectedImageUrl(nextImageUrl);
		setCrop({x: 0, y: 0});
		setZoom(1);
		setUploadMessage('');
		setShowSourcePopup(false);
		setShowCropPopup(true);
		event.target.value = '';
	};

	const handleUpload = async () => {
		if (!croppedAreaPixels || !selectedImageUrl) {
			setUploadMessage(labels.missingCropMessage);
			return;
		}

		if (!uploadUrl && !prepareOnly) {
			setUploadMessage(labels.uploadFailedMessage);
			return;
		}

		setUploadingPhoto(true);
		setUploadMessage('');

		try {
			const blob = await getCroppedImageBlob(selectedImageUrl, croppedAreaPixels);

			if (prepareOnly) {
				const preparedPhotoUrl = URL.createObjectURL(blob);
				updatePhoto(preparedPhotoUrl, {blob, photoUrl: preparedPhotoUrl, prepared: true});

				if (typeof onPreparedPhoto === 'function') {
					onPreparedPhoto(preparedPhotoUrl, {blob, photoUrl: preparedPhotoUrl, prepared: true});
				}

				closeCropPopup();
				return;
			}

			const formData = new FormData();
			formData.append(uploadFieldName, blob, uploadFileName);

			if (resolvedEntityKey !== '') {
				formData.append('entityKey', resolvedEntityKey);
			}

			const response = await axios({
				method: 'POST',
				url: uploadUrl,
				data: formData,
				withCredentials: true,
				headers: {
					'Content-Type': 'multipart/form-data',
					'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
				}
			});

			updatePhoto(response.data.photoUrl || '', response.data);
			closeCropPopup();
		} catch (error) {
			setUploadMessage(error?.response?.data?.message || error.message || labels.uploadFailedMessage);
		} finally {
			setUploadingPhoto(false);
		}
	};

	const handleDeletePhoto = async () => {
		if (!deleteUrl) {
			setUploadMessage(labels.deleteFailedMessage);
			setShowDeletePopup(false);
			return;
		}

		setDeletingPhoto(true);
		setUploadMessage('');

		try {
			const formData = new FormData();

			if (resolvedEntityKey !== '') {
				formData.append('entityKey', resolvedEntityKey);
			}

			const response = await axios({
				method: 'POST',
				url: deleteUrl,
				data: formData,
				withCredentials: true,
				headers: {
					'Content-Type': 'multipart/form-data',
					'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
				}
			});

			updatePhoto(response.data.photoUrl || '', response.data);
			setShowDeletePopup(false);
		} catch (error) {
			setUploadMessage(error?.response?.data?.message || error.message || labels.deleteFailedMessage);
			setShowDeletePopup(false);
		} finally {
			setDeletingPhoto(false);
		}
	};

	return (
		<>
			<div className="usrprofile_media" style={mediaStyle}>
				<button type="button" className="usrprofile_avatar usrprofile_avatar_preview_trigger" onClick={handlePreviewPhotoClick} style={avatarStyle}>
					{showPhotoImage ? (
						<img className="usrprofile_avatar_image" src={displayPhotoUrl} alt={displayName} onError={() => setImageLoadError(true)} />
					) : (
						<span className="usrprofile_avatar_initials fnt_2 fnt_b fnt_spec">{initials}</span>
					)}
				</button>
			</div>

			<input ref={selectInputRef} type="file" accept="image/*" className="usrprofile_input" onChange={handleImageSelected} />
			<input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="usrprofile_input" onChange={handleImageSelected} />

			{renderPopup(
				<ConfirmPopup showConfirmPopup={showSourcePopup} closeConfirmPopup={closeSourcePopup} infoHeader={labels.selectSourceHeader} infoMain={labels.selectSourceMessage} popupContentClassName="usrprofile_popup_content">
					<div className="usrprofile_popup_actions">
						<button type="button" className="button mr_1" onClick={() => selectInputRef.current?.click()}>{labels.choosePhotoButton}</button>
						<button type="button" className="button" onClick={() => cameraInputRef.current?.click()}>{labels.takePhotoButton}</button>
					</div>
				</ConfirmPopup>
			)}

			{renderPopup(
				<ConfirmPopup showConfirmPopup={showCropPopup} closeConfirmPopup={closeCropPopup} infoHeader={labels.cropHeader} infoMain="" popupContentClassName="usrprofile_popup_content">
					<div className="usrprofile_cropper_wrap">
						{selectedImageUrl !== '' ? (
							<div className="usrprofile_cropper_stage">
								<Cropper
									image={selectedImageUrl}
									crop={crop}
									zoom={zoom}
									aspect={1}
									onCropChange={setCrop}
									onZoomChange={setZoom}
									onCropComplete={(_ignored, croppedAreaPx) => setCroppedAreaPixels(croppedAreaPx)}
									showGrid={false}
								/>
							</div>
						) : (
							<div className="usrprofile_cropper_empty">{labels.emptyCropMessage}</div>
						)}

						<div className="usrprofile_cropper_controls">
							<label className="usrprofile_cropper_label" htmlFor={zoomRangeId}>{labels.zoomLabel}</label>
							<input id={zoomRangeId} type="range" min="1" max="3" step="0.01" value={zoom} onChange={(event) => setZoom(Number(event.target.value))} />
						</div>

						{uploadMessage !== '' ? (
							<div className="usrprofile_cropper_message">{uploadMessage}</div>
						) : (
							''
						)}

						<div className="usrprofile_popup_actions">
							<button type="button" className="button green mr_1" onClick={handleUpload} disabled={uploadingPhoto}>{uploadingPhoto ? labels.savingPhotoButton : labels.savePhotoButton}</button>
							<button type="button" className="button" onClick={closeCropPopup} disabled={uploadingPhoto}>{labels.cancelButton}</button>
						</div>
					</div>
				</ConfirmPopup>
			)}

			{renderPopup(
				<ConfirmPopup showConfirmPopup={showDeletePopup} showButtonDelete deleteConfirmPopup={handleDeletePhoto} closeConfirmPopup={closeDeletePopup} infoHeader={labels.deleteHeader} infoMain={labels.deleteMessage} popupContentClassName="usrprofile_popup_content">
					{deletingPhoto ? (
						<div className="usrprofile_cropper_message">{labels.deletingPhotoMessage}</div>
					) : (
						''
					)}
				</ConfirmPopup>
			)}

			{renderPopup(
				<ConfirmPopup
					showConfirmPopup={showPreviewPopup}
					closeConfirmPopup={closePreviewPopup}
					infoHeader="Profilová fotka"
					infoMain=""
					popupClassName="usrprofile_preview_popup"
					popupContentClassName="usrprofile_popup_content usrprofile_preview_popup_content"
					bodyClassName="usrprofile_preview_popup_body"
					bodyInnerClassName="usrprofile_preview_popup_body_in"
					footerChildren={(
						<>
							{canEditPhoto && showPhotoImage && deleteUrl ? (
								<button type="button" className="button red mr_2" onClick={handlePreviewDeleteClick}>
									{labels.previewDeleteButton}
								</button>
							) : (
								''
							)}

							{canEditPhoto && (uploadUrl || prepareOnly) ? (
								<button type="button" className="button green mr_2" onClick={handlePreviewUploadClick}>
									{labels.previewUploadButton}
								</button>
							) : (
								''
							)}
						</>
					)}
				>
					<div className="usrprofile_preview_stage">
						<div className="usrprofile_preview_circle">
							{showPhotoImage ? (
								<img className="usrprofile_preview_image" src={displayPhotoUrl} alt={displayName} onError={() => setImageLoadError(true)} />
							) : (
								<span className="usrprofile_preview_initials fnt_b fnt_spec">{initials}</span>
							)}
						</div>
					</div>
				</ConfirmPopup>
			)}
		</>
	);
}